import { evaluate } from "mathjs";
import { parseIntervalInput } from "../../../../components/inputs/IntervalInput";

function unwrap(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    if (value.target && "value" in value.target) return String(value.target.value ?? "");
    for (const key of ["raw", "text", "value", "latex", "expr"]) {
      if (typeof value[key] === "string") return value[key];
    }
  }
  return String(value);
}

function normalize(raw) {
  let s = unwrap(raw).trim().toLowerCase();
  s = s.replace(/\\left|\\right|\\bigl|\\bigr|\\!/g, "");
  s = s.replace(/\\infty|∞/g, "inf").replace(/\\cup|∪/g, "∪");
  s = s.replace(/\\sqrt\s*\{([^{}]+)\}/g, "sqrt($1)");
  s = s.replace(/\\sqrt\s*(\d+)/g, "sqrt($1)").replace(/√\s*(\d+)/g, "sqrt($1)");
  s = s.replace(/sqrt(\d+)/g, "sqrt($1)");
  for (let i = 0; i < 4; i++) {
    const next = s.replace(/\\(?:dfrac|tfrac|frac)\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, "(($1)/($2))");
    if (next === s) break;
    s = next;
  }
  return s.replace(/\\cdot|·|×/g, "*").replace(/\s+/g, "");
}

export function parseVietaNumber(raw) {
  let s = normalize(raw).replace(/\{,\}/g, ".");
  if (s === "-inf") return -Infinity;
  if (s === "+inf" || s === "inf") return Infinity;
  s = s.replace(/(\d)(sqrt\()/g, "$1*$2").replace(/\)(sqrt\()/g, ")*$1");
  if (!s || !/^[\d.+\-*/()sqrt]+$/.test(s)) return null;
  try {
    const number = evaluate(s);
    return typeof number === "number" && Number.isFinite(number) ? number : null;
  } catch {
    return null;
  }
}

export function parseVietaSet(raw) {
  let s = normalize(raw).replace(/\{,\}/g, ".");
  s = s.replace(/^(?:[mu]\s*(?:\\in|∈|in|=))/, "");
  if (!s) return null;
  const intervals = [];
  let pos = 0;
  while (pos < s.length) {
    const opener = s[pos];
    if (opener !== "(" && opener !== "[") return null;
    let depth = 0;
    let comma = -1;
    let end = -1;
    for (let i = pos; i < s.length; i++) {
      if (s[i] === "(" || s[i] === "[") depth++;
      if (s[i] === ")" || s[i] === "]") depth--;
      if (s[i] === "," && depth === 1 && comma < 0) comma = i;
      if (depth === 0) { end = i; break; }
    }
    if (comma < 0 || end < 0) return null;
    const left = parseVietaNumber(s.slice(pos + 1, comma));
    const right = parseVietaNumber(s.slice(comma + 1, end));
    if (left === null || right === null || left >= right) return null;
    const leftClosed = opener === "[";
    const rightClosed = s[end] === "]";
    if ((left === -Infinity && leftClosed) || (right === Infinity && rightClosed)) return null;
    intervals.push({ left, right, leftClosed, rightClosed });
    pos = end + 1;
    if (pos < s.length) {
      if (s[pos] !== "∪" && s[pos] !== "u" && s[pos] !== ";") return null;
      pos++;
    }
  }
  return intervals.sort((a, b) => a.left - b.left);
}

export function sameVietaSet(raw, expected) {
  const withoutVariable = unwrap(raw).trim().replace(/^[mu]\s*(?:\\in|∈|in|=)\s*/i, "");
  const shared = parseIntervalInput(withoutVariable);
  const actual = shared?.kind === "intervals" ? shared.intervals : parseVietaSet(raw);
  if (!actual || actual.length !== expected.length) return false;
  return actual.every((got, i) => {
    const want = expected[i];
    return (got.left === want.left || Math.abs(got.left - want.left) < 1e-8) &&
      (got.right === want.right || Math.abs(got.right - want.right) < 1e-8) &&
      got.leftClosed === want.leftClosed && got.rightClosed === want.rightClosed;
  });
}

export function sameVietaValue(raw, expected) {
  const s = normalize(raw).replace(/^m=/, "");
  const value = parseVietaNumber(s);
  return value !== null && Math.abs(value - expected) < 1e-8;
}
